/**
 * useDisplayController (SCN-612) — the whole device lifecycle in one place.
 *
 * booting → pairing (poll /status every 5s) → rotator (poll /me/playlist + heartbeat every 30s)
 *
 *  - register() runs only when there is no stored screenId
 *  - the pairing code shown on the TV is read from the /status response, never from /register
 *  - 404 from /status  → discard screenId, re-register
 *  - 401 from any authed endpoint → clear authToken only, keep screenId, back to pairing
 *  - 204 from /me/playlist → holding screen
 *  - network errors never change phase (the rotator keeps cycling the last playlist)
 *
 * All mutable lifecycle state lives in refs; the polling functions are created once in a mount
 * effect and close over those refs, so they always act on current values.
 */
import { useEffect, useRef, useState } from 'react';
import { getPlaylist, getStatus, registerScreen, sendHeartbeat } from '@/api/device';
import { DeviceApiError, DeviceNetworkError } from '@/api/client';
import { storage } from '@/storage';
import { HEARTBEAT_MS, PLAYLIST_POLL_MS, STATUS_POLL_MS } from '@/config';
import type { DevicePlaylist } from '@/types';

export type ControllerState =
  | { phase: 'booting' }
  | { phase: 'pairing'; pairingCode: string | null }
  | { phase: 'rotator'; playlist: DevicePlaylist }
  | { phase: 'holding' };

/** Poll intervals — overridable so tests can drive them deterministically. */
export interface ControllerOptions {
  statusMs?: number;
  playlistMs?: number;
  heartbeatMs?: number;
}

export function useDisplayController(options: ControllerOptions = {}): ControllerState {
  const [state, setState] = useState<ControllerState>({ phase: 'booting' });
  const optsRef = useRef(options);
  optsRef.current = options;

  const refs = useRef({
    screenId: storage.getScreenId(),
    token: null as string | null,
    /** The token a 401 just revoked — if /status hands the *same* string back, it is stale. */
    revokedToken: null as string | null,
    etag: null as string | null,
    mode: 'pairing' as 'pairing' | 'rotator',
    registering: false,
    timers: [] as ReturnType<typeof setInterval>[],
  });

  useEffect(() => {
    const r = refs.current;

    const clearTimers = () => {
      r.timers.forEach(clearInterval);
      r.timers = [];
    };

    /** Ensure a screenId exists, registering once if the slot is empty. */
    const ensureRegistered = async (): Promise<string | null> => {
      if (r.screenId) return r.screenId;
      if (r.registering) return null;
      r.registering = true;
      try {
        const { screenId } = await registerScreen();
        r.screenId = screenId;
        storage.setScreenId(screenId);
        return screenId;
      } catch (err) {
        if (err instanceof DeviceNetworkError) return null;
        throw err;
      } finally {
        r.registering = false;
      }
    };

    const startPairing = () => {
      r.mode = 'pairing';
      clearTimers();
      void pollStatus();
      r.timers.push(setInterval(() => void pollStatus(), optsRef.current.statusMs ?? STATUS_POLL_MS));
    };

    const enterRotator = () => {
      if (r.mode === 'rotator') return;
      r.mode = 'rotator';
      clearTimers();
      void pollPlaylist();
      void pollHeartbeat();
      r.timers.push(setInterval(() => void pollPlaylist(), optsRef.current.playlistMs ?? PLAYLIST_POLL_MS));
      r.timers.push(setInterval(() => void pollHeartbeat(), optsRef.current.heartbeatMs ?? HEARTBEAT_MS));
    };

    /** Token was revoked — drop it (keep screenId) and fall back to pairing. */
    const handleRevoked = () => {
      r.revokedToken = r.token;
      r.token = null;
      r.etag = null;
      storage.clearAuthToken();
      if (r.mode !== 'pairing') {
        setState({ phase: 'pairing', pairingCode: null });
        startPairing();
      }
    };

    async function pollStatus() {
      if (r.mode === 'rotator') return;

      let screenId: string | null;
      try {
        screenId = await ensureRegistered();
      } catch {
        return; // non-network register failure — retry next tick
      }
      if (!screenId) return;

      let status;
      try {
        status = await getStatus(screenId);
      } catch (err) {
        if (err instanceof DeviceNetworkError) return;
        if (err instanceof DeviceApiError && err.status === 404) {
          r.screenId = null;
          storage.clearScreenId();
          await ensureRegistered().catch(() => undefined);
          return;
        }
        return;
      }

      if (!status.isPaired) {
        storage.clearAuthToken();
        setState({ phase: 'pairing', pairingCode: status.pairingCode });
        return;
      }

      const token = status.authToken ?? storage.getAuthToken();
      if (!token || token === r.revokedToken) {
        // No usable token: past the claim window with nothing stored, or /status is still
        // handing back the exact token a 401 just rejected. Wait for an admin re-pair.
        setState({ phase: 'pairing', pairingCode: null });
        return;
      }
      r.token = token;
      r.revokedToken = null;
      storage.setAuthToken(token);
      enterRotator();
    }

    async function pollPlaylist() {
      if (!r.token) return;
      try {
        const res = await getPlaylist(r.token, r.etag);
        if (res.status === 304) return;
        r.etag = res.etag ?? r.etag;
        if (res.status === 204) {
          setState({ phase: 'holding' });
          return;
        }
        setState({ phase: 'rotator', playlist: res.data! });
      } catch (err) {
        if (err instanceof DeviceNetworkError) return;
        if (err instanceof DeviceApiError && err.status === 401) handleRevoked();
        // 429 / 5xx — ignore, retry next interval
      }
    }

    async function pollHeartbeat() {
      if (!r.token) return;
      try {
        await sendHeartbeat(r.token);
      } catch (err) {
        if (err instanceof DeviceApiError && err.status === 401) handleRevoked();
      }
    }

    startPairing();
    return clearTimers;
  }, []);

  return state;
}
